import React from 'react';
import { FaArrowRight } from 'react-icons/fa';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import useLazyImage from '../../hooks/useLazyImage';
import './Portfolio.css';

// Lazy Image Component
const LazyImage = ({ src, alt, className, onError }) => {
  const { elementRef, isLoaded, isInView, hasError } = useLazyImage(src);

  return (
    <div ref={elementRef} className={`lazy-image-container ${className}`}>
      {!isInView && (
        <div className="lazy-loading-placeholder">
          <div className="loading-spinner"></div>
        </div>
      )}
      {isInView && !isLoaded && !hasError && (
        <div className="lazy-loading-placeholder">
          <div className="loading-spinner"></div>
        </div>
      )}
      {isInView && isLoaded && !hasError && (
        <img 
          src={src} 
          alt={alt}
          className="lazy-loaded-image"
          onError={onError}
        />
      )}
      {hasError && (
        <div className="lazy-error-placeholder">
          <div className="error-icon">⚠️</div>
          <span>Image failed to load</span>
        </div>
      )}
    </div>
  );
};

const Portfolio = () => {
  const titleRef = useScrollAnimation();
  const portfolioImages = [
    {
      id: 1,
      src: '/images/portfolio/gallery/roofing-11.webp',
      alt: 'Professional Roofing Installation',
      category: 'Roofing',
      size: 'large'
    },
    {
      id: 2,
      src: '/images/portfolio/gallery/roofing-10.webp',
      alt: 'Quality Roofing Work',
      category: 'Roofing',
      size: 'medium'
    },
    {
      id: 3,
      src: '/images/portfolio/gallery/roofing-9.webp',
      alt: 'Residential Roofing Project',
      category: 'Roofing',
      size: 'medium'
    },
    {
      id: 4,
      src: '/images/portfolio/gallery/roofing-8.webp',
      alt: 'Commercial Roofing Service',
      category: 'Roofing',
      size: 'small'
    },
    {
      id: 5,
      src: '/images/portfolio/gallery/roofing-7.webp',
      alt: 'Expert Roofing Solutions',
      category: 'Roofing',
      size: 'small'
    }
  ];

  const handleViewMore = () => {
    // This will navigate to a gallery page in the same tab
    window.location.href = '/gallery';
  };

  return (
    <section id="portfolio" className="section portfolio pad-t-0" aria-labelledby="portfolio-title">
      <div className="container">
        <h2 id="portfolio-title" ref={titleRef} className="section-title ">Built to <span className="txtYellow2">Last</span></h2>
        <p className="section-subtitle">
        Explore our portfolio of recent roofing work across Detroit and nearby cities. Every roof meets our standard: durable, efficient, and designed to protect what matters most.
        </p>
        
        <div className="portfolio-collage">
          {portfolioImages.map((image) => (
            <div key={image.id} className={`portfolio-item ${image.size}`}>
              <div className="portfolio-image">
                <LazyImage 
                  src={image.src} 
                  alt={image.alt}
                  className="portfolio-lazy-image"
                  onError={(e) => {
                    // Fallback to placeholder if image doesn't exist
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="image-placeholder">
                  <div className="placeholder-icon">
                    {image.category === 'Roofing' && '🏠'}
                    {image.category === 'Siding' && '🏗️'}
                    {image.category === 'Gutters' && '🌧️'}
                    {image.category === 'Repairs' && '🔧'}
                    {image.category === 'Kitchen' && '🍳'}
                    {image.category === 'Bathroom' && '🛁'}
                    {image.category === 'Foundation' && '🏗️'}
                    {image.category === 'Plumbing' && '🔧'}
                    {image.category === 'Cement' && '🏗️'}
                    {image.category === 'Interior' && '🏠'}
                    {image.category === 'Planning' && '📋'}
                    {image.category === 'Structure' && '🔨'}
                    {image.category === 'Living' && '🛋️'}
                  </div>
                </div>
                <div className="portfolio-overlay">
                  <span className="portfolio-category">{image.category}</span>
                  <h3 className="portfolio-title">{image.alt}</h3>
                </div>
              </div>
            </div>
          ))}
          
          {/* View More Button as part of collage */}
          <div className="portfolio-item view-more-item" onClick={handleViewMore}>
            <div className="view-more-content">
              <div className="view-more-icon">
                <FaArrowRight />
              </div>
              <h3 className="view-more-title">View More</h3>
              <p className="view-more-subtitle">Explore our complete portfolio</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
